export const exportVenuePayrollCsv = ({ rows, venueName, weekStart }) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return { ok: false, reason: 'No payroll rows to export for this venue/week.' };
  }

  const headers = [
    'staff_name',
    'role',
    'shift_date',
    'clock_in_at',
    'clock_out_at',
    'break_minutes',
    'paid_hours',
    'status',
    'source',
    'venue_name',
  ];

  const csv = [
    headers.join(','),
    ...rows.map((row) =>
      headers
        .map((header) => {
          const raw = row?.[header] ?? '';
          const safe = String(raw).replaceAll('"', '""');
          return `"${safe}"`;
        })
        .join(',')
    ),
  ].join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `payroll-${venueName || 'venue'}-${weekStart}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);

  return { ok: true };
};

const renderCell = (value) => {
  const text = String(value ?? '');
  return text
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
};

export const exportVenuePayrollPdf = ({ rows, venueName, weekStart, weekLabel, totals }) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return { ok: false, reason: 'No payroll rows to export for this venue/week.' };
  }

  const popup = window.open('', '_blank', 'noopener,noreferrer,width=1200,height=800');
  if (!popup) {
    return { ok: false, reason: 'Unable to open print window. Allow pop-ups and try again.' };
  }

  const totalHours = Number(totals?.total_paid_hours || 0).toFixed(2);
  const totalShifts = Number(totals?.total_shifts || 0);
  const staffCount = Number(totals?.staff_count || 0);

  const rowsHtml = rows
    .map(
      (row) => `
      <tr>
        <td>${renderCell(row.staff_name || '-')}</td>
        <td>${renderCell(row.role || '-')}</td>
        <td>${renderCell(row.shift_date || '-')}</td>
        <td>${renderCell(row.clock_in_at || '-')}</td>
        <td>${renderCell(row.clock_out_at || '-')}</td>
        <td>${renderCell(row.break_minutes ?? 0)}</td>
        <td>${renderCell(Number(row.paid_hours || 0).toFixed(2))}</td>
        <td>${renderCell(row.status || '-')}</td>
        <td>${renderCell(row.source || '-')}</td>
      </tr>`
    )
    .join('');

  const html = `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Payroll ${renderCell(venueName || 'Venue')} ${renderCell(weekStart)}</title>
    <style>
      body { font-family: Arial, sans-serif; margin: 24px; color: #111; }
      h1 { margin: 0 0 8px; font-size: 24px; }
      .meta { margin-bottom: 16px; font-size: 13px; color: #444; }
      .summary { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin-bottom: 16px; }
      .summary-item { border: 1px solid #ddd; padding: 8px; border-radius: 4px; }
      .summary-label { font-size: 11px; color: #666; text-transform: uppercase; }
      .summary-value { font-size: 16px; font-weight: 700; margin-top: 4px; }
      table { width: 100%; border-collapse: collapse; font-size: 12px; }
      th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; }
      th { background: #f3f4f6; }
      @media print {
        body { margin: 12px; }
      }
    </style>
  </head>
  <body>
    <h1>Raise The Bar UK - Venue Payroll Report</h1>
    <div class="meta">
      Venue: ${renderCell(venueName || 'Venue')}<br/>
      Payroll Week: ${renderCell(weekLabel || weekStart)}<br/>
      Generated: ${renderCell(new Date().toLocaleString('en-GB', { timeZone: 'Europe/London' }))}
    </div>

    <div class="summary">
      <div class="summary-item">
        <div class="summary-label">Total Paid Hours</div>
        <div class="summary-value">${renderCell(totalHours)}</div>
      </div>
      <div class="summary-item">
        <div class="summary-label">Total Shifts</div>
        <div class="summary-value">${renderCell(totalShifts)}</div>
      </div>
      <div class="summary-item">
        <div class="summary-label">Staff Count</div>
        <div class="summary-value">${renderCell(staffCount)}</div>
      </div>
    </div>

    <table>
      <thead>
        <tr>
          <th>Staff</th>
          <th>Role</th>
          <th>Date</th>
          <th>Clock In</th>
          <th>Clock Out</th>
          <th>Break (min)</th>
          <th>Paid Hours</th>
          <th>Status</th>
          <th>Source</th>
        </tr>
      </thead>
      <tbody>
        ${rowsHtml}
      </tbody>
    </table>
  </body>
</html>`;

  popup.document.open();
  popup.document.write(html);
  popup.document.close();
  popup.focus();
  popup.print();

  return { ok: true };
};
